import React, { useState, useEffect, useRef } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { ThemeToggle } from '../theme/ThemeToggle'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { useQuery } from '@/apollo'
import { IS_ENTRA_ENABLED } from '../../graphql/auth'
import { GET_SYSTEM_INFO } from '../../graphql/system-info'
import { GET_PUBLIC_BRANDING } from '../../graphql/app-settings'
import { getGraphQLEndpoint } from '../../utils/graphqlConfig'

export const ModernLoginPage: React.FC = () => {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [mfaStep, setMfaStep] = useState(false)
  const [mfaToken, setMfaToken] = useState('')
  const [maskedEmail, setMaskedEmail] = useState('')
  const [mfaCode, setMfaCode] = useState('')
  // const [entraAuthUrl, setEntraAuthUrl] = useState<string | null>(null) // Reserved for future use
  const [isProcessingEntra, setIsProcessingEntra] = useState(false)
  const hasProcessedEntraRef = useRef(false)

  const { login, loginWithEntra, verifyLoginMfa, pendingMfaChallenge, clearPendingMfaChallenge } = useAuth()
  const navigate = useNavigate()
  const search = useSearch({ from: '/' })

  useEffect(() => {
    if (pendingMfaChallenge) {
      setMfaToken(pendingMfaChallenge.mfaToken)
      setMaskedEmail(pendingMfaChallenge.maskedEmail)
      setMfaStep(true)
    }
  }, [pendingMfaChallenge])

  // Check if error is an environment access error
  const isEnvironmentAccessError = error && (
    error.includes('Access denied') || 
    error.includes('permission to access') ||
    error.includes('environment access')
  )
  // Check if Entra ID is enabled
  const { data: entraData } = useQuery(IS_ENTRA_ENABLED)
  const isEntraEnabled = entraData?.isEntraEnabled || false

  // Public branding (company name) for login footer — no auth required
  const { data: brandingData } = useQuery(GET_PUBLIC_BRANDING, {
    errorPolicy: 'ignore',
    fetchPolicy: 'cache-first',
  })
  const companyName =
    brandingData?.publicBranding?.companyName || 'Healthcare Solutions Inc.'
  const copyrightYear = new Date().getFullYear()

  // Get environment name for watermark
  const { data: systemInfoData } = useQuery(GET_SYSTEM_INFO, {
    errorPolicy: 'ignore',
    fetchPolicy: 'cache-first'
  })
  
  const environmentName = systemInfoData?.systemInfo?.application?.environmentName || ''
  const buildTime = systemInfoData?.systemInfo?.application?.buildTime
  const gitCommit = systemInfoData?.systemInfo?.application?.gitCommit
  const isNonProductionEnv = ['Demo', 'PreProduction', 'PreProd', 'QA', 'Development'].includes(environmentName)
  const showWatermark = isNonProductionEnv
  const showBuildDate = (['Demo', 'PreProduction', 'PreProd', 'QA'].includes(environmentName)) && buildTime

  // Format build date as "12/13/2025 1:10 PM PST" in user's local timezone
  const formatBuildDate = (dateString: string): string => {
    const date = new Date(dateString)
    const month = date.getMonth() + 1
    const day = date.getDate()
    const year = date.getFullYear()
    const hours = date.getHours()
    const minutes = date.getMinutes()
    const ampm = hours >= 12 ? 'PM' : 'AM'
    const displayHours = hours % 12 || 12
    const displayMinutes = minutes.toString().padStart(2, '0')
    
    const timeZoneFormatter = new Intl.DateTimeFormat('en-US', {
      timeZoneName: 'short'
    })
    const timeZoneParts = timeZoneFormatter.formatToParts(date)
    const timeZone = timeZoneParts.find(part => part.type === 'timeZoneName')?.value || ''
    
    return `${month}/${day}/${year} ${displayHours}:${displayMinutes} ${ampm} ${timeZone}`
  }

  // Handle Entra callback on this page
  useEffect(() => {
    const searchParams = search as any
    const code = searchParams?.code
    const error = searchParams?.error
    const errorDescription = searchParams?.error_description
    const clientInfo = searchParams?.client_info
    const sessionState = searchParams?.session_state
    
    // Show loading overlay immediately if we detect any callback parameters
    if ((code || error || clientInfo || sessionState) && !hasProcessedEntraRef.current) {
      setIsProcessingEntra(true)
    }
    
    // Log all parameters for debugging
    if (code || error || clientInfo || sessionState) {
      console.log('Entra ID callback detected', {
        hasCode: !!code,
        hasError: !!error,
        hasClientInfo: !!clientInfo,
        hasSessionState: !!sessionState,
        error,
        errorDescription
      })
    }
    
    // Handle error from Microsoft
    if (error && !hasProcessedEntraRef.current) {
      hasProcessedEntraRef.current = true
      console.error('Entra ID error:', error, errorDescription)
      setError(`Microsoft authentication error: ${errorDescription || error}`)
      setIsProcessingEntra(false)
      
      // Clean up URL
      try {
        const url = new URL(window.location.href)
        url.searchParams.delete('error')
        url.searchParams.delete('error_description')
        url.searchParams.delete('client_info')
        url.searchParams.delete('session_state')
        window.history.replaceState({}, document.title, url.toString())
      } catch {}
      return
    }
    
    // Handle successful callback with code
    if (code && !hasProcessedEntraRef.current) {
      hasProcessedEntraRef.current = true
      setIsProcessingEntra(true)
      setError('')
      
      // Remove code from URL immediately
      try {
        const url = new URL(window.location.href)
        url.searchParams.delete('code')
        url.searchParams.delete('state')
        url.searchParams.delete('client_info')
        url.searchParams.delete('session_state')
        window.history.replaceState({}, document.title, url.toString())
      } catch {}

      // Process the Entra login
      const processEntraCallback = async () => {
        try {
          console.log('Processing Entra ID callback with code...')
          const success = await loginWithEntra(code)
          
          if (success) {
            console.log('Entra ID login successful')
            // Keep loading state until navigation completes
            // Navigate to dashboard
            setTimeout(() => {
              navigate({ to: '/dashboard' })
            }, 500)
          } else {
            console.error('Entra ID login failed - success was false')
            setError('Microsoft authentication failed. Please try again.')
            setIsProcessingEntra(false)
          }
        } catch (err: any) {
          console.error('Entra callback error:', err)
          if (!err.message?.includes('already processed')) {
            setError(err.message || 'Microsoft authentication failed')
          }
          setIsProcessingEntra(false)
        }
      }

      processEntraCallback()
    } else if ((clientInfo || sessionState) && !code && !hasProcessedEntraRef.current) {
      // Microsoft returned client_info/session_state but no code - this usually means an error
      hasProcessedEntraRef.current = true
      console.warn('Entra ID callback received client_info/session_state but no code')
      setError('Microsoft authentication incomplete. Please try signing in again.')
      setIsProcessingEntra(false)
      
      // Clean up URL
      try {
        const url = new URL(window.location.href)
        url.searchParams.delete('client_info')
        url.searchParams.delete('session_state')
        window.history.replaceState({}, document.title, url.toString())
      } catch {}
    }
  }, [search, loginWithEntra, navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    try {
      const result = await login({ username, password })
      
      if (result.requiresMfa && result.mfaToken) {
        setMfaToken(result.mfaToken)
        setMaskedEmail(result.maskedEmail || '')
        setMfaStep(true)
        setMfaCode('')
        return
      }

      if (result.success) {
        navigate({ to: '/dashboard' })
        return
      }

      if (!result.success) {
        setError(result.errorMessage || 'Login failed. Please check your credentials.')
      }
    } catch (err: any) {
      setError(err.message || 'Login failed. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    try {
      const result = await verifyLoginMfa(mfaToken, mfaCode, () => {
        navigate({ to: '/dashboard' })
      })

      if (!result.success) {
        setError(result.errorMessage || 'Invalid or expired verification code.')
      }
    } catch (err: any) {
      setError(err.message || 'Verification failed. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleBackToLogin = () => {
    clearPendingMfaChallenge()
    setMfaStep(false)
    setMfaToken('')
    setMaskedEmail('')
    setMfaCode('')
    setError('')
  }

  const handleEntraLogin = async () => {
    try {
      setIsLoading(true)
      setError('')
      
      // Use environment-aware GraphQL endpoint configuration
      const graphqlUrl = getGraphQLEndpoint()
      
      const response = await fetch(graphqlUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: `query { getEntraAuthUrl }`
        })
      })
      
      const { data } = await response.json()
      
      if (data?.getEntraAuthUrl) {
        // Redirect to Microsoft login
        window.location.href = data.getEntraAuthUrl
      } else {
        setError('Entra ID authentication is not available')
      }
    } catch (err: any) {
      setError('Failed to initialize Entra ID login')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
      <div className="fixed top-4 right-4 z-10">
        <ThemeToggle />
      </div>
      
      {/* Entra Authentication Overlay - Shows over login form during Microsoft redirect */}
      {isProcessingEntra && (
        <div 
          className="fixed inset-0 flex items-center justify-center z-[9999]"
          style={{ 
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            backdropFilter: 'blur(8px)'
          }}
        >
          <div className="card p-8 text-center max-w-sm mx-4" style={{ 
            backgroundColor: 'var(--color-bg-primary)',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2)'
          }}>
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-t-transparent mx-auto mb-4" style={{ 
              borderColor: 'var(--color-accent)',
              borderTopColor: 'transparent'
            }}></div>
            <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--color-text-primary)' }}>
              Signing you in...
            </h2>
            <p style={{ color: 'var(--color-text-secondary)' }}>
              Completing Microsoft authentication
            </p>
          </div>
        </div>
      )}
      
      <div className={`card max-w-md w-full p-8 ${isProcessingEntra ? 'opacity-50 pointer-events-none' : ''}`}>
        <h1 className="text-3xl font-bold text-center mb-2" style={{ color: 'var(--color-text-primary)' }}>Welcome back</h1>
        <p className="text-center mb-8" style={{ color: 'var(--color-text-secondary)' }}>Sign in to your account to continue</p>

        {/* Environment Access Error Banner - Shows above login form */}
        {isEnvironmentAccessError && (
          <div 
            className="px-4 py-4 rounded-lg mb-6 border-l-4" 
            style={{ 
              backgroundColor: 'rgba(251, 191, 36, 0.1)', 
              borderLeftColor: '#fbbf24',
              border: '1px solid rgba(251, 191, 36, 0.3)',
              color: '#f59e0b'
            }}
          >
            <div className="flex items-start">
              <svg 
                className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0" 
                fill="currentColor" 
                viewBox="0 0 20 20"
              >
                <path 
                  fillRule="evenodd" 
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" 
                  clipRule="evenodd" 
                />
              </svg>
              <div>
                <div className="font-semibold mb-1">Access Permission Required</div>
                <div className="text-sm">
                  You need permission from an administrator to access this environment. Please contact your system administrator to request access.
                </div>
              </div>
            </div>
          </div>
        )}

        {mfaStep ? (
          <form onSubmit={handleMfaSubmit} className="space-y-6">
            {error && (
              <div
                className="px-4 py-3 rounded-lg text-sm mb-4"
                style={{
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  color: '#ef4444',
                }}
              >
                <strong>Error:</strong> {error}
              </div>
            )}

            <div
              className="px-4 py-3 rounded-lg text-sm"
              style={{
                backgroundColor: 'var(--color-bg-secondary)',
                border: '1px solid var(--color-border)',
                color: 'var(--color-text-secondary)',
              }}
            >
              We sent a 6-digit verification code to{' '}
              <strong style={{ color: 'var(--color-text-primary)' }}>{maskedEmail}</strong>.
              Enter it below to finish signing in.
            </div>

            <div>
              <label htmlFor="mfaCode" className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>
                Verification code
              </label>
              <input
                id="mfaCode"
                name="mfaCode"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                required
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="input-field tracking-widest text-center text-lg"
                placeholder="000000"
                disabled={isLoading || isProcessingEntra}
              />
            </div>

            <button
              type="submit"
              disabled={isLoading || mfaCode.length !== 6 || isProcessingEntra}
              className="btn-primary w-full"
            >
              {isLoading ? 'Verifying...' : 'Verify and sign in'}
            </button>

            <button
              type="button"
              onClick={handleBackToLogin}
              className="w-full text-sm"
              style={{ color: 'var(--color-text-secondary)' }}
              disabled={isLoading}
            >
              Back to sign in
            </button>
          </form>
        ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Regular error display (for non-environment access errors) */}
          {error && !isEnvironmentAccessError && (
            <div 
              className="px-4 py-3 rounded-lg text-sm mb-4" 
              style={{ 
                backgroundColor: 'rgba(239, 68, 68, 0.1)', 
                border: '1px solid rgba(239, 68, 68, 0.3)',
                color: '#ef4444'
              }}
            >
              <strong>Error:</strong> {error}
            </div>
          )}

          <div>
            <label htmlFor="username" className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>
              Username or Email
            </label>
            <input
              id="username"
              name="username"
              type="text"
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="input-field"
              placeholder="Enter your username"
              disabled={isLoading || isProcessingEntra}
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              className="input-field pr-12"
              placeholder="Enter your password"
              disabled={isLoading || isProcessingEntra}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center transition-colors"
                style={{ color: 'var(--color-text-muted)' }}
                disabled={isLoading || isProcessingEntra}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading || isProcessingEntra}
            className="btn-primary w-full"
          >
            {isLoading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>
        )}

        {!mfaStep && isEntraEnabled && (
          <>
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t" style={{ borderColor: 'var(--color-border)' }}></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2" style={{ backgroundColor: 'var(--color-bg-primary)', color: 'var(--color-text-muted)' }}>
                  Or continue with
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={handleEntraLogin}
              disabled={isLoading || isProcessingEntra}
              className="w-full flex items-center justify-center px-4 py-2 border rounded-lg transition-colors"
              style={{
                borderColor: 'var(--color-border)',
                backgroundColor: 'var(--color-bg-secondary)',
                color: 'var(--color-text-primary)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
              }}
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
              </svg>
              Sign in with Microsoft
            </button>
          </>
        )}

        <p className="text-center text-sm mt-8" style={{ color: 'var(--color-text-muted)' }}>
          © {copyrightYear} {companyName}. All rights reserved.
        </p>
      </div>

      {/* Environment Watermark */}
      {showWatermark && (
        <div
          style={{
            position: 'fixed',
            bottom: '20px',
            left: '20px',
            zIndex: 9999,
            padding: '8px 16px',
            borderRadius: '4px',
            backgroundColor: 'transparent',
            color: 'var(--color-text-primary)',
            opacity: 0.6,
            pointerEvents: 'none',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '4px'
          }}
        >
          <div
            style={{
              fontWeight: 'bold',
              fontSize: '14px',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}
          >
            {environmentName}
          </div>
          {showBuildDate && buildTime && (
            <>
              <div
                style={{
                  fontSize: '11px',
                  fontWeight: 'normal',
                  textTransform: 'none',
                  letterSpacing: '0.5px',
                  opacity: 0.8
                }}
              >
                Build Date: {formatBuildDate(buildTime)}
              </div>
              {gitCommit && (
                <div
                  style={{
                    fontSize: '11px',
                    fontWeight: 'normal',
                    textTransform: 'none',
                    letterSpacing: '0.5px',
                    opacity: 0.8
                  }}
                >
                  {gitCommit}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}